import { useEffect, useState } from "react";
import { MapPin, ImagePlus, Send, CheckCircle2, ExternalLink, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Reveal from "../components/Reveal";

const BIZ_CATEGORIES = [
  "Trades & Construction", "Tech & Digital", "Food & Drink", "Health & Fitness",
  "Motors & Auto", "Style & Grooming", "Home & Interiors", "Professional Services",
  "Creative & Media", "Other",
];

export default function DirectoryPage() {
  const [entries, setEntries] = useState(null);
  const [cat, setCat] = useState("all");
  const [form, setForm] = useState({ company: "", category: "Trades & Construction", location: "", website: "", card_image: "" });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setEntries(null);
    const params = cat === "all" ? {} : { category: cat };
    api.get("/directory", { params }).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, [cat]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/directory/upload", fd);
      setForm((f) => ({ ...f, card_image: data.url }));
      toast.success("Business card uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.card_image) {
      toast.error("Upload your business card image first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/directory", form);
      try {
        const co = await api.post("/payments/checkout", {
          lookup_key: "directory_listing_gbp10",
          origin_url: window.location.origin,
          entry_id: data.id,
        });
        window.location.href = co.data.checkout_url;
        return;
      } catch (e) {
        toast.error("Listing saved, but checkout failed to start — our editors will still review it.");
      }
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="directory-page" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-4">The Black Book</p>
        <h1 className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl tracking-tight break-words">
          UK Business <span className="text-outline">Directory</span>
        </h1>
        <p className="text-ash mt-5 max-w-xl leading-relaxed">
          Britain's sharpest independent businesses, vouched for by this magazine's readers.
          Submit your card — our editors review every listing before it goes live.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-14 mb-20">
        <Reveal className="lg:col-span-5">
          <div className="border border-white/10 bg-surface p-6 lg:p-8 h-full">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-crimson mb-2">Advertise here</p>
            <h2 className="font-display font-extrabold uppercase text-2xl tracking-tight mb-6">List your business</h2>

            {done ? (
              <div data-testid="directory-success" className="border border-kinetic/40 bg-kinetic/5 p-6 flex items-start gap-4">
                <CheckCircle2 className="text-kinetic shrink-0 mt-0.5" size={24} />
                <div>
                  <p className="font-display font-bold text-lg">Card received.</p>
                  <p className="text-ash text-sm mt-1">Our editors review every submission. Once approved, your business appears in the directory below.</p>
                  <button onClick={() => { setDone(false); setForm({ company: "", category: "Trades & Construction", location: "", website: "", card_image: "" }); }}
                    className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-kinetic hover:text-porcelain transition-colors">
                    Submit another →
                  </button>
                </div>
              </div>
            ) : (
              <form data-testid="directory-form" onSubmit={submit} className="flex flex-col gap-3">
                <input data-testid="directory-company-input" value={form.company} required minLength={2} maxLength={80}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name"
                  className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors" />
                <div className="flex flex-wrap gap-2">
                  {BIZ_CATEGORIES.map((c) => (
                    <button type="button" key={c} data-testid={`directory-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setForm({ ...form, category: c })}
                      className={`text-[10px] font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border transition-colors ${form.category === c ? "bg-kinetic text-obsidian border-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
                      {c}
                    </button>
                  ))}
                </div>
                <input data-testid="directory-location-input" value={form.location} required minLength={2} maxLength={80}
                  onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location — City / County"
                  className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors" />
                <input data-testid="directory-website-input" value={form.website} required type="url" minLength={8}
                  onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website URL — https://…"
                  className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors" />

                {form.card_image ? (
                  <div className="relative border border-white/10">
                    <img src={form.card_image} alt="Your business card" className="w-full max-h-44 object-cover" />
                    <button type="button" data-testid="directory-card-remove" onClick={() => setForm({ ...form, card_image: "" })}
                      className="absolute top-2 right-2 bg-obsidian/80 backdrop-blur px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-crimson border border-crimson/40 hover:bg-crimson hover:text-porcelain transition-colors">
                      Remove
                    </button>
                  </div>
                ) : (
                  <label data-testid="directory-card-upload-label"
                    className={`flex flex-col items-center justify-center gap-2.5 border border-dashed py-8 cursor-pointer transition-colors ${uploading ? "border-kinetic/50 text-kinetic" : "border-white/20 text-ash hover:border-kinetic/60 hover:text-porcelain"}`}>
                    <ImagePlus size={20} />
                    <span className="text-[11px] font-mono uppercase tracking-[0.2em]">
                      {uploading ? "Uploading card…" : "Upload your business card"}
                    </span>
                    <span className="text-[10px] font-mono text-ash/60">JPG, PNG or WebP · max 8 MB</span>
                    <input data-testid="directory-card-upload-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden" onChange={handleUpload} disabled={uploading} />
                  </label>
                )}

                <button data-testid="directory-submit-button" type="submit" disabled={busy || uploading}
                  className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium py-4 rounded-full flex items-center justify-center gap-2 hover:bg-porcelain transition-colors duration-300 disabled:opacity-50 mt-1">
                  {busy ? "Submitting…" : "Submit for review"} <Send size={14} />
                </button>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="border border-kinetic/30 bg-kinetic/[0.04] p-6 lg:p-8 h-full flex flex-col justify-center">
            <Building2 className="text-kinetic mb-5" size={28} />
            <h3 className="font-display font-extrabold uppercase text-xl tracking-tight mb-4">Why list with us?</h3>
            <ul className="space-y-4 text-sm text-ash leading-relaxed">
              <li><span className="text-porcelain font-medium">A curated wall, not a phone book.</span> Every card is reviewed by an editor — if it's here, it earned the slot.</li>
              <li><span className="text-porcelain font-medium">Readers who spend.</span> Our audience buys gear, books trips and hires people who take pride in the work.</li>
              <li><span className="text-porcelain font-medium">One-off £10, no subscriptions.</span> Your card, your link, our readers — listed for good after a single payment and a quick editorial review.</li>
            </ul>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">Approved businesses</h2>
          {entries && (
            <p data-testid="directory-count" className="text-[11px] font-mono uppercase tracking-[0.2em] text-ash">
              {entries.length} listed
            </p>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8">
          <button data-testid="directory-filter-all" onClick={() => setCat("all")}
            className={`whitespace-nowrap text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-2 rounded-full border transition-colors ${cat === "all" ? "bg-kinetic text-obsidian border-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
            All trades
          </button>
          {BIZ_CATEGORIES.map((c) => (
            <button key={c} data-testid={`directory-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setCat(c)}
              className={`whitespace-nowrap text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-2 rounded-full border transition-colors ${cat === c ? "bg-kinetic text-obsidian border-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
              {c}
            </button>
          ))}
        </div>
      </Reveal>

      {!entries ? (
        <p className="font-mono text-ash text-xs uppercase tracking-[0.3em] py-20 text-center animate-pulse">Loading the wall…</p>
      ) : entries.length === 0 ? (
        <div data-testid="directory-empty" className="border border-dashed border-white/15 py-20 text-center">
          <Building2 size={26} className="text-ash mx-auto mb-4" />
          <p className="font-mono text-ash text-xs uppercase tracking-[0.3em]">No listings here yet — be the first on the wall.</p>
        </div>
      ) : (
        <div data-testid="directory-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {entries.map((d, i) => (
            <Reveal key={d.id} delay={(i % 6) * 0.05}>
              <div data-testid="directory-card" className="group border border-white/10 bg-surface hover:border-kinetic/40 transition-colors duration-300 overflow-hidden">
                <div className="aspect-[7/4] overflow-hidden bg-obsidian flex items-center justify-center">
                  <img src={d.card_image} alt={`${d.company} business card`} loading="lazy"
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700 ease-out" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-kinetic">{d.category}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg leading-snug">{d.company}</h3>
                  <p className="flex items-center gap-1.5 text-ash text-xs font-mono mt-2">
                    <MapPin size={12} /> {d.location}
                  </p>
                  <a href={d.website} target="_blank" rel="noopener noreferrer sponsored" data-testid="directory-visit-link"
                    className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-porcelain border border-white/15 hover:border-kinetic/60 hover:text-kinetic px-4 py-2.5 rounded-full transition-colors duration-300">
                    Visit site <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
